import { InjectionToken } from '@angular/core';
import type { SentryReportTags } from './problem-kind';
import type { FatalErrorPageParams } from 'pages/fatal-error/fatal-error-interface';

/** What `DatabaseRecoveryService` needs from the reporting service, and nothing else.
 *
 * `LoggingService` is what really answers this, and it is the full description of what each
 * method means — this is only the slice database recovery calls. Keep it that narrow: a member
 * added here is a member the two services are coupled by. */
export interface RecoveryReportingService {
  /** File a problem the user is never shown. See `LoggingService.trackSilentProblem`. */
  trackSilentProblem(problemName: string, context: any, tags?: SentryReportTags): void;
  /** Give up and show the user the fatal-error page. See `LoggingService.fatalError`. */
  fatalError(params: FatalErrorPageParams): Promise<void>;
}

/** How database recovery asks for the reporting service, instead of being handed it.
 *
 * It exists because the two services call each other — reporting asks database recovery about a
 * database error as a safety net, and database recovery asks reporting to file what it finds — so
 * neither can take the other through its constructor; Angular cannot build a dependency cycle.
 * Database recovery therefore looks this token up at the moment it reports something, by which
 * time both services already exist.
 *
 * Why a token rather than asking Angular for `LoggingService` by name: `database-recovery.service.ts`
 * imports `LoggingService` as a TYPE only, so that the two files have no runtime import cycle
 * between them. Naming the class in a lookup needs the class as a runtime value and would put that
 * cycle back. This file imports neither service, so there is no cycle to reason about.
 *
 * `app.module.ts` is the one place that knows both halves, and it already imported both. */
export const RECOVERY_REPORTING_SERVICE = new InjectionToken<RecoveryReportingService>('RecoveryReportingService');
